import { apiClient } from "./apiClient";

export interface AuthUser {
  id: string;
  email: string;
  role: "user" | "admin" | "super_admin";
  full_name?: string;
  avatar_url?: string;
}

type AuthSession = {
  user: {
    id: string;
    email: string;
  };
  profile: {
    id: string;
    user_id: string;
    role: "user" | "admin" | "super_admin";
    full_name: string | null;
    avatar_url: string | null;
    created_at: string;
    updated_at: string;
  };
  token?: string;
};

type AuthStateChangeCallback = (_event: string, session: AuthSession | null) => void;

const listeners = new Set<AuthStateChangeCallback>();
const AUTH_SESSION_CACHE_KEY = "finance_hub_auth_session";
const ROLE_SET = new Set(["user", "admin", "super_admin"]);

type CurrentAuth = {
  user: AuthSession["user"];
  profile: AuthSession["profile"];
};

const cacheSession = (session: CurrentAuth) => {
  try {
    localStorage.setItem(AUTH_SESSION_CACHE_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage write errors in restricted browser environments.
  }
};

const clearCachedSession = () => {
  try {
    localStorage.removeItem(AUTH_SESSION_CACHE_KEY);
  } catch {
    // Ignore storage write errors in restricted browser environments.
  }
};

const readCachedSession = (): CurrentAuth | null => {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as CurrentAuth;
    if (!parsed?.user?.id || !parsed?.user?.email || !parsed?.profile?.role) {
      return null;
    }

    if (!ROLE_SET.has(parsed.profile.role)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) {
      return null;
    }

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = atob(padded);
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getSessionFromToken = (token: string): CurrentAuth | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const userId = String(payload.userId || "").trim();
  const email = String(payload.email || "").trim();
  const role = String(payload.role || "").trim() as AuthSession["profile"]["role"];

  if (!userId || !email || !ROLE_SET.has(role)) {
    return null;
  }

  const now = new Date().toISOString();
  return {
    user: {
      id: userId,
      email,
    },
    profile: {
      id: `token-profile-${userId}`,
      user_id: userId,
      role,
      full_name: null,
      avatar_url: null,
      created_at: now,
      updated_at: now,
    },
  };
};

const notifyAuthChange = async (event: string) => {
  const session = await authService.getCurrentUser();
  listeners.forEach((callback) => callback(event, session as AuthSession | null));
};

export const authService = {
  async signIn(email: string, password: string) {
    const session = await apiClient.post<AuthSession>("/auth/signin", { email, password });
    if (session.token) {
      apiClient.setAuthToken(session.token);
    }

    cacheSession({ user: session.user, profile: session.profile });

    await notifyAuthChange("SIGNED_IN");
    return { user: session.user, profile: session.profile };
  },

  async signUp(email: string, password: string, fullName?: string) {
    const session = await apiClient.post<AuthSession>("/auth/signup", {
      email,
      password,
      full_name: fullName,
    });

    if (session.token) {
      apiClient.setAuthToken(session.token);
    }

    cacheSession({ user: session.user, profile: session.profile });

    await notifyAuthChange("SIGNED_UP");
    return session;
  },

  async signOut() {
    try {
      await apiClient.post<void>("/auth/signout");
    } finally {
      apiClient.setAuthToken(null);
      clearCachedSession();
      await notifyAuthChange("SIGNED_OUT");
    }
  },

  async getCurrentUser() {
    const token = apiClient.getAuthToken();
    if (!token) {
      return null;
    }

    try {
      const session = await apiClient.get<AuthSession>("/auth/me");
      if (session.token) {
        apiClient.setAuthToken(session.token);
      }

      const current = {
        user: session.user,
        profile: session.profile,
      };
      cacheSession(current);
      return current;
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (status === 401) {
        apiClient.setAuthToken(null);
        clearCachedSession();
        return null;
      }

      const cached = readCachedSession();
      if (cached) {
        return cached;
      }

      return getSessionFromToken(token);
    }
  },

  async isAdmin(): Promise<boolean> {
    const current = await this.getCurrentUser();
    return current?.profile?.role === "admin" || current?.profile?.role === "super_admin";
  },

  async isSuperAdmin(): Promise<boolean> {
    const current = await this.getCurrentUser();
    return current?.profile?.role === "super_admin";
  },

  onAuthStateChange(callback: AuthStateChangeCallback) {
    listeners.add(callback);

    return {
      data: {
        subscription: {
          unsubscribe: () => listeners.delete(callback),
        },
      },
    };
  },

  async resetPassword(email: string) {
    await apiClient.post<{ message: string }>("/auth/reset-password", { email });
  },

  async updateUserMetadata(metadata: Record<string, any>) {
    const profile = await apiClient.put<any>("/auth/metadata", metadata);
    await notifyAuthChange("USER_UPDATED");
    return { user: null, profile };
  },

  async updateAccount(payload: { email?: string; password?: string }) {
    const session = await apiClient.put<AuthSession>("/auth/account", payload);
    if (session.token) {
      apiClient.setAuthToken(session.token);
    }

    cacheSession({ user: session.user, profile: session.profile });

    await notifyAuthChange("USER_UPDATED");
    return session;
  },
};

export const adminLogin = async (email: string, password: string): Promise<boolean> => {
  try {
    const result = await authService.signIn(email, password);
    return result.profile.role === "admin" || result.profile.role === "super_admin";
  } catch (error) {
    console.error("Admin login failed:", error);
    return false;
  }
};

export const adminLogout = async (): Promise<void> => {
  try {
    await authService.signOut();
  } catch (error) {
    console.error("Admin logout failed:", error);
  }
};
